import React, { useState } from 'react';
import {
  Grid, Card, CardContent, Typography, Box, TextField, Button,
  Switch, Divider, Alert, LinearProgress, Chip,
  List, ListItem, ListItemButton, ListItemText, IconButton,
  FormControl, InputLabel, Select, MenuItem,
  useTheme,
} from '@mui/material';
import {
  Settings as SettingsIcon, Notifications as NotificationsIcon,
  Security as SecurityIcon, Business as BusinessIcon,
  Palette as PaletteIcon, Save as SaveIcon,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  Language as BookingIcon,
  Payment as PaymentIcon,
  RateReview as RateReviewIcon,
  Hub as WebhookIcon,
  CalendarMonth as CalendarIcon,
  ContentCopy as ContentCopyIcon,
  EmailOutlined as EmailOutlinedIcon,
  MenuBook as MenuBookIcon,
  PictureAsPdf as PictureAsPdfIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { API_BASE_URL } from '../api';
import { APP_VERSION } from '../components/AppFooter';
import KeyboardShortcutsDialog from '../components/KeyboardShortcutsDialog';
import { LARAVEL_VERSION_LABEL, REACT_VERSION_LABEL } from '../constants/buildInfo';
import { brandHeaderStyle, resolvePublicBrand } from '../utils/publicBrand';

const TIMEZONE_CHOICES = ['UTC', 'Europe/Sofia', 'Europe/London', 'Europe/Paris', 'America/New_York'];

/** Training manuals in public/docs (HTML + PDF, full content). */
const DOCS_BASE = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/docs`;
const USER_GUIDES = [
  {
    id: 'en-html',
    label: 'English — full guide (web page)',
    href: `${DOCS_BASE}/workwisesoft-user-guide-en.html`,
    pdf: false,
  },
  {
    id: 'en-pdf',
    label: 'English — full guide (PDF download)',
    href: `${DOCS_BASE}/workwisesoft-user-guide-en.pdf`,
    pdf: true,
  },
  {
    id: 'bg-html',
    label: 'Български — пълно ръководство (уеб страница)',
    href: `${DOCS_BASE}/workwisesoft-user-guide-bg.html`,
    pdf: false,
  },
  {
    id: 'bg-pdf',
    label: 'Български — пълно ръководство (PDF за изтегляне)',
    href: `${DOCS_BASE}/workwisesoft-user-guide-bg.pdf`,
    pdf: true,
  },
];

async function getCurrentUserApi() {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}user`, {
    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || 'Could not load user');
  return data;
}

async function putUserPreferences(partial) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}user/preferences`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(partial),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || data.error || `Save failed (${res.status})`);
  }
  try {
    const raw = localStorage.getItem('user');
    if (raw && data.ui_preferences) {
      const u = JSON.parse(raw);
      u.ui_preferences = data.ui_preferences;
      localStorage.setItem('user', JSON.stringify(u));
    }
  } catch {
    /* ignore */
  }
  return data;
}

function mergeLocalSettingsBackup(userId, patch) {
  if (!userId) return;
  const key = `user_settings_${userId}`;
  try {
    const cur = JSON.parse(localStorage.getItem(key) || '{}');
    localStorage.setItem(key, JSON.stringify({ ...cur, ...patch }));
  } catch {
    /* ignore */
  }
}

export default function Settings({
  darkMode,
  updateSettings,
  locale,
  setLocale,
  tableDensity,
  setTableDensity,
  timezone,
  setTimezone,
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [initialPrefsLoading, setInitialPrefsLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [currentUser, setCurrentUser] = useState(null);

  React.useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setCurrentUser(parsedUser);
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, []);

  // General Settings (user-specific)
  const [companyName, setCompanyName] = useState('WorkWiseSoft');
  const [companyEmail, setCompanyEmail] = useState('admin@workwisesoft.com');
  const [companyPhone, setCompanyPhone] = useState('+44 20 7946 0958');
  const [companyAddress, setCompanyAddress] = useState('123 Business St, City, Country');

  // Notification Settings (user-specific)
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [jobUpdates, setJobUpdates] = useState(true);
  const [invoiceReminders, setInvoiceReminders] = useState(true);

  const roleName = currentUser?.role?.name || currentUser?.role || '';
  const isSuperAdmin = roleName === 'super_admin';
  const canManageInvoicePayments = ['super_admin', 'admin'].includes(roleName);

  const [bookingNotifyEmail, setBookingNotifyEmail] = useState('');
  const [bookingOfficeEmailEnabled, setBookingOfficeEmailEnabled] = useState(true);
  const [bookingOfficePushEnabled, setBookingOfficePushEnabled] = useState(true);
  const [bookingClientEmailEnabled, setBookingClientEmailEnabled] = useState(true);
  const [bookingSettingsLoaded, setBookingSettingsLoaded] = useState(false);
  const [bookingSettingsLoading, setBookingSettingsLoading] = useState(false);
  const [bookingSettingsError, setBookingSettingsError] = useState('');
  const [bookingTurnstileEnabled, setBookingTurnstileEnabled] = useState(false);
  const [bookingTurnstileSiteKey, setBookingTurnstileSiteKey] = useState('');
  const [bookingTurnstileSecretInput, setBookingTurnstileSecretInput] = useState('');
  const [bookingTurnstileSecretSet, setBookingTurnstileSecretSet] = useState(false);
  const [bookingEmbedEnabled, setBookingEmbedEnabled] = useState(false);
  const [bookingEmbedOrigins, setBookingEmbedOrigins] = useState('');
  const [bookingIframeSnippet, setBookingIframeSnippet] = useState('');
  const [bookingScriptSnippet, setBookingScriptSnippet] = useState('');
  const [snippetCopied, setSnippetCopied] = useState('');

  const [invoicePayStripeEnabled, setInvoicePayStripeEnabled] = useState(false);
  const [invoicePayStripeConfigured, setInvoicePayStripeConfigured] = useState(false);
  const [invoicePayBankName, setInvoicePayBankName] = useState('');
  const [invoicePayBankSortCode, setInvoicePayBankSortCode] = useState('');
  const [invoicePayBankAccount, setInvoicePayBankAccount] = useState('');
  const [invoicePayBankReferenceHint, setInvoicePayBankReferenceHint] = useState('');
  const [invoicePayInstructions, setInvoicePayInstructions] = useState('');
  const [invoicePaySettingsLoaded, setInvoicePaySettingsLoaded] = useState(false);
  const [invoicePaySettingsLoading, setInvoicePaySettingsLoading] = useState(false);
  const [invoicePaySettingsError, setInvoicePaySettingsError] = useState('');

  const [brandCompanyName, setBrandCompanyName] = useState('');
  const [brandCompanyAddress, setBrandCompanyAddress] = useState('');
  const [brandCompanyEmail, setBrandCompanyEmail] = useState('');
  const [brandCompanyPhone, setBrandCompanyPhone] = useState('');
  const [brandCompanyReg, setBrandCompanyReg] = useState('');
  const [brandCompanyVat, setBrandCompanyVat] = useState('');
  const [brandLogoUrl, setBrandLogoUrl] = useState('');
  const [brandPrimaryColor, setBrandPrimaryColor] = useState('#1565C0');
  const [brandAccentColor, setBrandAccentColor] = useState('#42A5F5');
  const [brandBackgroundColor, setBrandBackgroundColor] = useState('#F0F4F8');
  const [brandSettingsLoaded, setBrandSettingsLoaded] = useState(false);
  const [brandSettingsLoading, setBrandSettingsLoading] = useState(false);
  const [brandSettingsError, setBrandSettingsError] = useState('');

  const [reputationEnabled, setReputationEnabled] = useState(false);
  const [reputationReviewUrl, setReputationReviewUrl] = useState('');
  const [reputationDelayMinutes, setReputationDelayMinutes] = useState(1440);
  const [reputationChannel, setReputationChannel] = useState('email');
  const [reputationRuleId, setReputationRuleId] = useState(null);
  const [reputationSettingsLoaded, setReputationSettingsLoaded] = useState(false);
  const [reputationSettingsLoading, setReputationSettingsLoading] = useState(false);
  const [reputationSettingsError, setReputationSettingsError] = useState('');

  const [outboundSmsEnabled, setOutboundSmsEnabled] = useState(false);
  const [outboundEmailEnabled, setOutboundEmailEnabled] = useState(false);
  const [outboundTwilioOk, setOutboundTwilioOk] = useState(false);
  const [outboundMailOk, setOutboundMailOk] = useState(false);
  const [outboundSettingsLoading, setOutboundSettingsLoading] = useState(false);
  const [outboundSettingsError, setOutboundSettingsError] = useState('');

  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookSecretSet, setWebhookSecretSet] = useState(false);
  const [webhookEvents, setWebhookEvents] = useState(['job.completed', 'estimate.accepted', 'invoice.paid']);
  const [webhookDeliveries, setWebhookDeliveries] = useState([]);
  const [webhookSettingsLoaded, setWebhookSettingsLoaded] = useState(false);
  const [webhookSettingsLoading, setWebhookSettingsLoading] = useState(false);
  const [webhookSettingsError, setWebhookSettingsError] = useState('');
  const [webhookTestLoading, setWebhookTestLoading] = useState(false);

  const [calendarEnabled, setCalendarEnabled] = useState(false);
  const [calendarFeedUrl, setCalendarFeedUrl] = useState('');
  const [calendarTimezone, setCalendarTimezone] = useState('Europe/London');
  const [calendarDaysAhead, setCalendarDaysAhead] = useState(90);
  const [calendarDaysBack, setCalendarDaysBack] = useState(7);
  const [calendarSettingsLoaded, setCalendarSettingsLoaded] = useState(false);
  const [calendarSettingsLoading, setCalendarSettingsLoading] = useState(false);
  const [calendarSettingsError, setCalendarSettingsError] = useState('');
  const [calendarFeedCopied, setCalendarFeedCopied] = useState(false);

  // Security Settings (user-specific)
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [localDarkMode, setLocalDarkMode] = useState(darkMode);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [emailDigest, setEmailDigest] = useState('off');

  React.useEffect(() => {
    setLocalDarkMode(darkMode);
  }, [darkMode]);

  const applyPrefsObject = React.useCallback((settings) => {
    if (!settings || typeof settings !== 'object') return;
    if (settings.companyName != null) setCompanyName(settings.companyName);
    if (settings.companyEmail != null) setCompanyEmail(settings.companyEmail);
    if (settings.companyPhone != null) setCompanyPhone(settings.companyPhone);
    if (settings.companyAddress != null) setCompanyAddress(settings.companyAddress);
    if (settings.emailNotifications !== undefined) setEmailNotifications(!!settings.emailNotifications);
    if (settings.smsNotifications !== undefined) setSmsNotifications(!!settings.smsNotifications);
    if (settings.jobUpdates !== undefined) setJobUpdates(!!settings.jobUpdates);
    if (settings.invoiceReminders !== undefined) setInvoiceReminders(!!settings.invoiceReminders);
    if (settings.darkMode !== undefined) setLocalDarkMode(!!settings.darkMode);
    if (settings.emailDigest === 'off' || settings.emailDigest === 'daily' || settings.emailDigest === 'weekly') {
      setEmailDigest(settings.emailDigest);
    }
    if (settings.locale === 'en' || settings.locale === 'bg') {
      setLocale('en');
      localStorage.setItem('appLocale', 'en');
    }
    if (settings.timezone && typeof settings.timezone === 'string') {
      setTimezone(settings.timezone);
      localStorage.setItem('appTimezone', settings.timezone);
    }
    if (settings.tableDensity === 'comfortable' || settings.tableDensity === 'compact') {
      setTableDensity(settings.tableDensity);
      localStorage.setItem('tableDensity', settings.tableDensity);
    }
  }, [setLocale, setTimezone, setTableDensity]);

  // Load from API (account), fallback: legacy localStorage for this browser
  React.useEffect(() => {
    if (!currentUser?.id) {
      setInitialPrefsLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setInitialPrefsLoading(true);
      setError('');
      const legacy = (() => {
        try {
          return JSON.parse(localStorage.getItem(`user_settings_${currentUser.id}`) || '{}');
        } catch {
          return {};
        }
      })();
      try {
        const data = await getCurrentUserApi();
        if (cancelled) return;
        if (data.user) {
          setCurrentUser(data.user);
        }
        try {
          const rawU = localStorage.getItem('user');
          const prev = rawU ? JSON.parse(rawU) : {};
          localStorage.setItem('user', JSON.stringify({ ...prev, ...data.user }));
        } catch {
          /* ignore */
        }
        const server = data.user?.ui_preferences;
        const useServer = server && typeof server === 'object' && Object.keys(server).length > 0;
        const merged = useServer ? { ...legacy, ...server } : legacy;
        applyPrefsObject(merged);
        if (Object.prototype.hasOwnProperty.call(merged, 'darkMode')) {
          updateSettings(!!merged.darkMode);
        }
      } catch (e) {
        if (!cancelled) {
          applyPrefsObject(legacy);
          if (Object.prototype.hasOwnProperty.call(legacy, 'darkMode')) {
            updateSettings(!!legacy.darkMode);
          }
          setError(
            (e && e.message) ||
              'Could not load settings from the server. Showing data saved in this browser. Run database migrations if this persists.'
          );
        }
      } finally {
        if (!cancelled) setInitialPrefsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, applyPrefsObject, updateSettings]);

  React.useEffect(() => {
    if (!isSuperAdmin) {
      setBookingSettingsLoaded(false);
      setBookingSettingsLoading(false);
      setBookingSettingsError('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setBookingSettingsLoading(true);
      setBookingSettingsError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}settings/online-booking-notifications`, {
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          const status = res.status;
          let msg = json.message || `Could not load settings (HTTP ${status})`;
          if (status === 500) {
            msg =
              'Server error (HTTP 500). Restart the Laravel API after setting JWT_SECRET in backend/.env (php artisan jwt:secret --force), then log out and log in again.';
          } else if (status === 401) {
            msg = 'Session expired. Log out and log in again.';
          }
          throw new Error(msg);
        }
        if (cancelled) return;
        const d = json.data || {};
        setBookingNotifyEmail(d.office_notify_email || '');
        setBookingOfficeEmailEnabled(!!d.office_email_enabled);
        setBookingOfficePushEnabled(!!d.office_push_enabled);
        setBookingClientEmailEnabled(d.client_email_enabled !== false);
        setBookingTurnstileEnabled(!!d.turnstile_enabled);
        setBookingTurnstileSiteKey(d.turnstile_site_key || '');
        setBookingTurnstileSecretSet(!!d.turnstile_secret_key_set);
        setBookingTurnstileSecretInput('');
      } catch (e) {
        if (!cancelled) {
          const msg = e.message || 'Could not load online booking notification settings';
          setBookingSettingsError(msg);
          if (msg.includes('403') || msg.toLowerCase().includes('super admin')) {
            setBookingSettingsError(
              `${msg} Log in with a super admin account (not admin/manager) to edit these settings.`
            );
          }
        }
      } finally {
        if (!cancelled) {
          setBookingSettingsLoading(false);
          setBookingSettingsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  React.useEffect(() => {
    if (!canManageInvoicePayments) return undefined;
    let cancelled = false;
    (async () => {
      setInvoicePaySettingsLoading(true);
      setInvoicePaySettingsError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}settings/invoice-payments`, {
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.message || `Could not load invoice payment settings (HTTP ${res.status})`);
        if (cancelled) return;
        const d = json.data || {};
        setInvoicePayStripeEnabled(!!d.stripe_enabled);
        setInvoicePayStripeConfigured(!!d.stripe_configured);
        setInvoicePayBankName(d.bank_name || '');
        setInvoicePayBankSortCode(d.bank_sort_code || '');
        setInvoicePayBankAccount(d.bank_account || '');
        setInvoicePayBankReferenceHint(d.bank_reference_hint || '');
        setInvoicePayInstructions(d.instructions || '');
      } catch (e) {
        if (!cancelled) setInvoicePaySettingsError(e.message || 'Could not load invoice payment settings');
      } finally {
        if (!cancelled) {
          setInvoicePaySettingsLoading(false);
          setInvoicePaySettingsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageInvoicePayments]);

  React.useEffect(() => {
    if (!canManageInvoicePayments) return undefined;
    let cancelled = false;
    (async () => {
      setBrandSettingsLoading(true);
      setBrandSettingsError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}settings/public-brand`, {
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.message || `Could not load brand settings (HTTP ${res.status})`);
        if (cancelled) return;
        const d = json.data || {};
        setBrandCompanyName(d.company_name || '');
        setBrandCompanyAddress(d.company_address || '');
        setBrandCompanyEmail(d.company_email || '');
        setBrandCompanyPhone(d.company_phone || '');
        setBrandCompanyReg(d.company_reg_number || '');
        setBrandCompanyVat(d.company_vat_number || '');
        setBrandLogoUrl(d.logo_url || '');
        setBrandPrimaryColor(d.primary_color || '#1565C0');
        setBrandAccentColor(d.accent_color || '#42A5F5');
        setBrandBackgroundColor(d.background_color || '#F0F4F8');
      } catch (e) {
        if (!cancelled) setBrandSettingsError(e.message || 'Could not load brand settings');
      } finally {
        if (!cancelled) {
          setBrandSettingsLoading(false);
          setBrandSettingsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageInvoicePayments]);

  React.useEffect(() => {
    if (!canManageInvoicePayments) return undefined;
    let cancelled = false;
    (async () => {
      setReputationSettingsLoading(true);
      setReputationSettingsError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}settings/reputation`, {
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.message || `Could not load reputation settings (HTTP ${res.status})`);
        if (cancelled) return;
        const d = json.data || {};
        setReputationEnabled(!!d.enabled);
        setReputationReviewUrl(d.google_review_url || '');
        setReputationDelayMinutes(d.delay_minutes ?? 1440);
        setReputationChannel(d.channel === 'sms' ? 'sms' : 'email');
        setReputationRuleId(d.automation_rule_id || null);
      } catch (e) {
        if (!cancelled) setReputationSettingsError(e.message || 'Could not load reputation settings');
      } finally {
        if (!cancelled) {
          setReputationSettingsLoading(false);
          setReputationSettingsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageInvoicePayments]);

  React.useEffect(() => {
    if (!canManageInvoicePayments) return undefined;
    let cancelled = false;
    (async () => {
      setOutboundSettingsLoading(true);
      setOutboundSettingsError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}settings/outbound-messaging`, {
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.message || `Could not load outbound messaging (HTTP ${res.status})`);
        if (cancelled) return;
        const d = json.data || {};
        setOutboundSmsEnabled(!!d.manual_sms_enabled);
        setOutboundEmailEnabled(!!d.manual_email_enabled);
        setOutboundTwilioOk(!!d.twilio_configured);
        setOutboundMailOk(!!d.mail_configured);
      } catch (e) {
        if (!cancelled) setOutboundSettingsError(e.message || 'Could not load outbound messaging');
      } finally {
        if (!cancelled) setOutboundSettingsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageInvoicePayments]);

  React.useEffect(() => {
    if (!canManageInvoicePayments) return undefined;
    let cancelled = false;
    (async () => {
      setWebhookSettingsLoading(true);
      setWebhookSettingsError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}settings/webhooks`, {
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.message || `Could not load webhook settings (HTTP ${res.status})`);
        if (cancelled) return;
        const d = json.data || {};
        setWebhookEnabled(!!d.enabled);
        setWebhookUrl(d.url || '');
        setWebhookSecretSet(!!d.secret_set);
        setWebhookEvents(Array.isArray(d.events) && d.events.length ? d.events : ['job.completed', 'estimate.accepted', 'invoice.paid']);
        setWebhookDeliveries(json.recent_deliveries || []);
      } catch (e) {
        if (!cancelled) setWebhookSettingsError(e.message || 'Could not load webhook settings');
      } finally {
        if (!cancelled) {
          setWebhookSettingsLoading(false);
          setWebhookSettingsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageInvoicePayments]);

  React.useEffect(() => {
    if (!canManageInvoicePayments) return undefined;
    let cancelled = false;
    (async () => {
      setCalendarSettingsLoading(true);
      setCalendarSettingsError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}settings/calendar`, {
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.message || `Could not load calendar settings (HTTP ${res.status})`);
        if (cancelled) return;
        const d = json.data || {};
        setCalendarEnabled(!!d.enabled);
        setCalendarFeedUrl(d.feed_url || '');
        setCalendarTimezone(d.timezone || 'Europe/London');
        setCalendarDaysAhead(d.days_ahead ?? 90);
        setCalendarDaysBack(d.days_back ?? 7);
      } catch (e) {
        if (!cancelled) setCalendarSettingsError(e.message || 'Could not load calendar settings');
      } finally {
        if (!cancelled) {
          setCalendarSettingsLoading(false);
          setCalendarSettingsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageInvoicePayments]);

  const handleSaveOutboundMessaging = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}settings/outbound-messaging`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          manual_sms_enabled: outboundSmsEnabled,
          manual_email_enabled: outboundEmailEnabled,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Save failed');
      if (json.data) {
        setOutboundSmsEnabled(!!json.data.manual_sms_enabled);
        setOutboundEmailEnabled(!!json.data.manual_email_enabled);
        setOutboundTwilioOk(!!json.data.twilio_configured);
        setOutboundMailOk(!!json.data.mail_configured);
      }
      setSuccess(json.message || 'Outbound messaging settings saved.');
    } catch (e) {
      setError(e.message || 'Could not save outbound messaging settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReputation = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}settings/reputation`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          enabled: reputationEnabled,
          google_review_url: reputationReviewUrl.trim(),
          delay_minutes: Number(reputationDelayMinutes) || 0,
          channel: reputationChannel,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Save failed');
      if (json.data) {
        setReputationRuleId(json.data.automation_rule_id || null);
        setReputationEnabled(!!json.data.enabled);
      }
      setSuccess(json.message || 'Reputation settings saved. Automation rule updated.');
    } catch (e) {
      setError(e.message || 'Could not save reputation settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWebhooks = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const body = {
        enabled: webhookEnabled,
        url: webhookUrl.trim(),
        events: webhookEvents,
      };
      if (webhookSecret.trim()) {
        body.secret = webhookSecret.trim();
      }
      const res = await fetch(`${API_BASE_URL}settings/webhooks`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Save failed');
      if (json.data) {
        setWebhookEnabled(!!json.data.enabled);
        setWebhookSecretSet(!!json.data.secret_set);
        setWebhookSecret('');
      }
      setSuccess(json.message || 'Webhook settings saved.');
    } catch (e) {
      setError(e.message || 'Could not save webhook settings');
    } finally {
      setLoading(false);
    }
  };

  const handleWebhookTestPing = async () => {
    setWebhookTestLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}settings/webhooks/test`, {
        method: 'POST',
        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Test webhook failed');
      if (json.delivery) {
        setWebhookDeliveries((prev) => [json.delivery, ...prev.filter((d) => d.id !== json.delivery.id)].slice(0, 10));
      }
      setSuccess(json.message || 'Test webhook sent.');
    } catch (e) {
      setError(e.message || 'Test webhook failed');
    } finally {
      setWebhookTestLoading(false);
    }
  };

  const toggleWebhookEvent = (eventName) => {
    setWebhookEvents((prev) => (
      prev.includes(eventName)
        ? prev.filter((e) => e !== eventName)
        : [...prev, eventName]
    ));
  };

  const handleSaveCalendar = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}settings/calendar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          enabled: calendarEnabled,
          timezone: calendarTimezone,
          days_ahead: Number(calendarDaysAhead) || 90,
          days_back: Number(calendarDaysBack) || 7,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Save failed');
      if (json.data) {
        setCalendarEnabled(!!json.data.enabled);
        setCalendarFeedUrl(json.data.feed_url || '');
      }
      setSuccess(json.message || 'Calendar settings saved.');
    } catch (e) {
      setError(e.message || 'Could not save calendar settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRotateCalendarToken = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}settings/calendar/rotate-token`, {
        method: 'POST',
        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Could not regenerate feed URL');
      if (json.data?.feed_url) setCalendarFeedUrl(json.data.feed_url);
      setSuccess(json.message || 'Feed URL regenerated.');
    } catch (e) {
      setError(e.message || 'Could not regenerate feed URL');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBookingNotifications = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}settings/online-booking-notifications`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          office_notify_email: bookingNotifyEmail.trim(),
          office_email_enabled: bookingOfficeEmailEnabled,
          office_push_enabled: bookingOfficePushEnabled,
          client_email_enabled: bookingClientEmailEnabled,
          turnstile_enabled: bookingTurnstileEnabled,
          turnstile_site_key: bookingTurnstileSiteKey.trim(),
          ...(bookingTurnstileSecretInput.trim()
            ? { turnstile_secret_key: bookingTurnstileSecretInput.trim() }
            : {}),
          embed_enabled: bookingEmbedEnabled,
          embed_allowed_origins: bookingEmbedOrigins,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);
      const d = json.data || {};
      setBookingNotifyEmail(d.office_notify_email || '');
      setBookingOfficeEmailEnabled(!!d.office_email_enabled);
      setBookingOfficePushEnabled(!!d.office_push_enabled);
      setBookingClientEmailEnabled(!!d.client_email_enabled);
      setBookingTurnstileEnabled(!!d.turnstile_enabled);
      setBookingTurnstileSiteKey(d.turnstile_site_key || '');
      setBookingTurnstileSecretSet(!!d.turnstile_secret_key_set);
      setBookingTurnstileSecretInput('');
      setBookingEmbedEnabled(!!d.embed_enabled);
      setBookingEmbedOrigins(d.embed_allowed_origins || '');
      setBookingIframeSnippet(d.iframe_snippet || '');
      setBookingScriptSnippet(d.script_snippet || '');
      setBookingSettingsError('');
      setSuccess(json.message || 'Online booking settings saved.');
    } catch (err) {
      setBookingSettingsError(err.message || 'Failed to save online booking settings');
      setError(err.message || 'Failed to save online booking settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInvoicePayments = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}settings/invoice-payments`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          stripe_enabled: invoicePayStripeEnabled,
          bank_name: invoicePayBankName.trim(),
          bank_sort_code: invoicePayBankSortCode.trim(),
          bank_account: invoicePayBankAccount.trim(),
          bank_reference_hint: invoicePayBankReferenceHint.trim(),
          instructions: invoicePayInstructions.trim(),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);
      const d = json.data || {};
      setInvoicePayStripeEnabled(!!d.stripe_enabled);
      setInvoicePayStripeConfigured(!!d.stripe_configured);
      setInvoicePayBankName(d.bank_name || '');
      setInvoicePayBankSortCode(d.bank_sort_code || '');
      setInvoicePayBankAccount(d.bank_account || '');
      setInvoicePayBankReferenceHint(d.bank_reference_hint || '');
      setInvoicePayInstructions(d.instructions || '');
      setInvoicePaySettingsError('');
      setSuccess(json.message || 'Invoice payment settings saved.');
    } catch (err) {
      setInvoicePaySettingsError(err.message || 'Failed to save invoice payment settings');
      setError(err.message || 'Failed to save invoice payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePublicBrand = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}settings/public-brand`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          company_name: brandCompanyName.trim(),
          company_address: brandCompanyAddress.trim(),
          company_email: brandCompanyEmail.trim(),
          company_phone: brandCompanyPhone.trim(),
          company_reg_number: brandCompanyReg.trim(),
          company_vat_number: brandCompanyVat.trim(),
          logo_url: brandLogoUrl.trim(),
          primary_color: brandPrimaryColor.trim(),
          accent_color: brandAccentColor.trim(),
          background_color: brandBackgroundColor.trim(),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);
      const d = json.data || {};
      setBrandCompanyName(d.company_name || '');
      setBrandLogoUrl(d.logo_url || '');
      setBrandPrimaryColor(d.primary_color || '#1565C0');
      setBrandAccentColor(d.accent_color || '#42A5F5');
      setBrandBackgroundColor(d.background_color || '#F0F4F8');
      setBrandSettingsError('');
      setSuccess(json.message || 'Public brand settings saved.');
    } catch (err) {
      setBrandSettingsError(err.message || 'Failed to save brand settings');
      setError(err.message || 'Failed to save brand settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const patch = { companyName, companyEmail, companyPhone, companyAddress };
      await putUserPreferences(patch);
      mergeLocalSettingsBackup(currentUser?.id, patch);
      setSuccess('General settings saved to your account.');
    } catch (err) {
      setError(err.message || 'Failed to save general settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const patch = {
        emailNotifications,
        smsNotifications,
        jobUpdates,
        invoiceReminders,
        emailDigest,
      };
      await putUserPreferences(patch);
      mergeLocalSettingsBackup(currentUser?.id, patch);
      setSuccess('Notification preferences saved to your account.');
    } catch (err) {
      setError(err.message || 'Failed to save notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSecurity = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    if (newPassword && newPassword !== confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }
    if (!currentPassword?.trim() || !newPassword?.trim()) {
      setError('Enter your current password and a new password.');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_BASE_URL}users/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          new_password_confirmation: confirmPassword,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setError(err.message || err.error || 'Failed to change password');
        setLoading(false);
        return;
      }

      setSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message || 'Failed to save security settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAppearance = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const patch = { darkMode: localDarkMode };
      await putUserPreferences(patch);
      mergeLocalSettingsBackup(currentUser?.id, patch);
      setSuccess('Theme preference saved to your account.');
    } catch (err) {
      setError(err.message || 'Failed to save theme preference');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRegional = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const patch = {
        locale: 'en',
        timezone,
        tableDensity,
      };
      setLocale('en');
      localStorage.setItem('appLocale', 'en');
      await putUserPreferences(patch);
      mergeLocalSettingsBackup(currentUser?.id, patch);
      setSuccess('Regional and table settings saved.');
    } catch (err) {
      setError(err.message || 'Failed to save regional settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            Settings
          </Typography>
        </Box>
        <Typography 
          variant="h6" 
          sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 500,
            fontSize: '1rem',
            lineHeight: 1.4,
          }}
        >
          Manage your account settings and preferences.
        </Typography>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {(loading || initialPrefsLoading) && <LinearProgress sx={{ mb: 3 }} />}

      {/* Settings List */}
      <Grid container spacing={3}>
        {/* General Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ 
                  width: 48, height: 48, borderRadius: '12px', 
                  background: 'linear-gradient(135deg, #3b82f620 0%, #3b82f610 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#3b82f6'
                }}>
                  <BusinessIcon />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: '1.5rem' }}>
                    General Settings
                  </Typography>
                  <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '0.875rem' }}>
                    Company information and basic details
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Email"
                    type="email"
                    value={companyEmail}
                    onChange={(e) => setCompanyEmail(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Phone"
                    value={companyPhone}
                    onChange={(e) => setCompanyPhone(e.target.value)}
                    placeholder="+44 20 7946 0958"
                    helperText="UK format (e.g. +44 or 07…)"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Address"
                    multiline
                    rows={3}
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                    sx={{ mb: 3 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5, lineHeight: 1.5 }}>
                    Saved to your user account on the server. Shown for reference in invoices/UI where the app uses them.
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<SaveIcon />}
                    onClick={handleSaveGeneral}
                    disabled={loading}
                    sx={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                      },
                    }}
                  >
                    Save General Settings
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ 
                  width: 48, height: 48, borderRadius: '12px', 
                  background: 'linear-gradient(135deg, #f59e0b20 0%, #f59e0b10 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#f59e0b'
                }}>
                  <NotificationsIcon />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    Notification Settings
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Manage your notification preferences
                  </Typography>
                </Box>
              </Box>

              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 2, lineHeight: 1.5 }}>
                Saved on your account. Actual email/SMS delivery still depends on server mail/SMS configuration; job/invoice alerts use this when backend support is added.
              </Typography>

              <List sx={{ p: 0 }}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary="Email Notifications"
                    secondary="Receive notifications via email"
                  />
                  <Switch
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    color="primary"
                  />
                </ListItem>
                <Divider />
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary="SMS Notifications"
                    secondary="Receive notifications via SMS"
                  />
                  <Switch
                    checked={smsNotifications}
                    onChange={(e) => setSmsNotifications(e.target.checked)}
                    color="primary"
                  />
                </ListItem>
                <Divider />
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary="Job Updates"
                    secondary="Get notified about job status changes"
                  />
                  <Switch
                    checked={jobUpdates}
                    onChange={(e) => setJobUpdates(e.target.checked)}
                    color="primary"
                  />
                </ListItem>
                <Divider />
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary="Invoice Reminders"
                    secondary="Get reminded about pending invoices"
                  />
                  <Switch
                    checked={invoiceReminders}
                    onChange={(e) => setInvoiceReminders(e.target.checked)}
                    color="primary"
                  />
                </ListItem>
              </List>

              <FormControl fullWidth sx={{ mt: 2 }} size="small">
                <InputLabel id="email-digest-label">Email digest</InputLabel>
                <Select
                  labelId="email-digest-label"
                  label="Email digest"
                  value={emailDigest}
                  onChange={(e) => setEmailDigest(e.target.value)}
                >
                  <MenuItem value="off">Off</MenuItem>
                  <MenuItem value="daily">Daily summary (server schedule)</MenuItem>
                  <MenuItem value="weekly">Weekly summary (server schedule)</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                fullWidth
                startIcon={<SaveIcon />}
                onClick={handleSaveNotifications}
                disabled={loading}
                sx={{
                  mt: 3,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  },
                }}
              >
                Save Notification Settings
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {isSuperAdmin ? (
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid', borderColor: 'primary.light' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #1565c020 0%, #1565c010 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#1565c0',
                    }}
                  >
                    <BookingIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Online booking
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Alerts and anti-abuse (Cloudflare Turnstile) for the public booking page. Super admin only.
                    </Typography>
                  </Box>
                </Box>

                {bookingSettingsLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
                {bookingSettingsError ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {bookingSettingsError}
                    {' '}
                    You can still edit and save below; defaults apply until the server accepts your changes.
                  </Alert>
                ) : null}

                <TextField
                  fullWidth
                  label="Office notification email"
                  type="email"
                  value={bookingNotifyEmail}
                  onChange={(e) => setBookingNotifyEmail(e.target.value)}
                  helperText="Where to send new booking alerts. Required if office email alerts are on."
                  sx={{ mb: 2 }}
                  disabled={bookingSettingsLoading}
                />

                <List sx={{ p: 0, mb: 2 }}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Email alerts to office"
                      secondary="Send an email to the address above for each new online booking"
                    />
                    <Switch
                      checked={bookingOfficeEmailEnabled}
                      onChange={(e) => setBookingOfficeEmailEnabled(e.target.checked)}
                      color="primary"
                      disabled={bookingSettingsLoading}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Browser push to office staff"
                      secondary="Notify users with super admin, admin, office admin, or manager role who enabled push in the app"
                    />
                    <Switch
                      checked={bookingOfficePushEnabled}
                      onChange={(e) => setBookingOfficePushEnabled(e.target.checked)}
                      color="primary"
                      disabled={bookingSettingsLoading}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Confirmation email to customer"
                      secondary="Automatic “we received your request” email (no payment online)"
                    />
                    <Switch
                      checked={bookingClientEmailEnabled}
                      onChange={(e) => setBookingClientEmailEnabled(e.target.checked)}
                      color="primary"
                      disabled={bookingSettingsLoading}
                    />
                  </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  CAPTCHA (Cloudflare Turnstile)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Protects the booking form from bots. Create keys at{' '}
                  <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noreferrer">
                    Cloudflare Turnstile
                  </a>
                  . Leave disabled until both keys are saved.
                </Typography>
                <List sx={{ p: 0, mb: 2 }}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Require CAPTCHA on booking submit"
                      secondary={
                        bookingTurnstileSecretSet && bookingTurnstileSiteKey.trim()
                          ? 'Active when enabled and keys are valid'
                          : 'Add site key and secret key first'
                      }
                    />
                    <Switch
                      checked={bookingTurnstileEnabled}
                      onChange={(e) => setBookingTurnstileEnabled(e.target.checked)}
                      color="primary"
                      disabled={bookingSettingsLoading}
                    />
                  </ListItem>
                </List>
                <TextField
                  fullWidth
                  label="Turnstile site key (public)"
                  value={bookingTurnstileSiteKey}
                  onChange={(e) => setBookingTurnstileSiteKey(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={bookingSettingsLoading}
                />
                <TextField
                  fullWidth
                  label="Turnstile secret key"
                  type="password"
                  value={bookingTurnstileSecretInput}
                  onChange={(e) => setBookingTurnstileSecretInput(e.target.value)}
                  helperText={
                    bookingTurnstileSecretSet
                      ? 'Secret is stored. Enter a new value only to replace it.'
                      : 'Required before enabling CAPTCHA'
                  }
                  sx={{ mb: 2 }}
                  disabled={bookingSettingsLoading}
                />

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Embed widget
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Paste the booking form on your website (iframe or script). List each parent site origin below
                  (one per line) for API CORS. See docs/EMBED_BOOKING.md.
                </Typography>
                <List sx={{ p: 0, mb: 2 }}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Allow embedding on external sites"
                      secondary="When off, snippets still work on your app domain only"
                    />
                    <Switch
                      checked={bookingEmbedEnabled}
                      onChange={(e) => setBookingEmbedEnabled(e.target.checked)}
                      color="primary"
                      disabled={bookingSettingsLoading}
                    />
                  </ListItem>
                </List>
                <TextField
                  fullWidth
                  label="Allowed embed origins"
                  value={bookingEmbedOrigins}
                  onChange={(e) => setBookingEmbedOrigins(e.target.value)}
                  multiline
                  minRows={3}
                  placeholder={'https://www.yourwebsite.co.uk\nhttps://localhost:3000'}
                  helperText="Scheme + host + optional port, one per line"
                  sx={{ mb: 2 }}
                  disabled={bookingSettingsLoading}
                />
                {bookingIframeSnippet ? (
                  <TextField
                    fullWidth
                    label="Iframe snippet"
                    value={bookingIframeSnippet}
                    multiline
                    minRows={4}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 1 }}
                  />
                ) : null}
                {bookingIframeSnippet ? (
                  <Button
                    size="small"
                    sx={{ mb: 2, textTransform: 'none' }}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(bookingIframeSnippet);
                        setSnippetCopied('iframe');
                        setTimeout(() => setSnippetCopied(''), 2000);
                      } catch {
                        /* ignore */
                      }
                    }}
                  >
                    {snippetCopied === 'iframe' ? 'Copied!' : 'Copy iframe snippet'}
                  </Button>
                ) : null}
                {bookingScriptSnippet ? (
                  <TextField
                    fullWidth
                    label="Script snippet"
                    value={bookingScriptSnippet}
                    multiline
                    minRows={4}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 1 }}
                  />
                ) : null}
                {bookingScriptSnippet ? (
                  <Button
                    size="small"
                    sx={{ mb: 2, textTransform: 'none' }}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(bookingScriptSnippet);
                        setSnippetCopied('script');
                        setTimeout(() => setSnippetCopied(''), 2000);
                      } catch {
                        /* ignore */
                      }
                    }}
                  >
                    {snippetCopied === 'script' ? 'Copied!' : 'Copy script snippet'}
                  </Button>
                ) : null}

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveBookingNotifications}
                  disabled={loading || bookingSettingsLoading}
                  sx={{ fontWeight: 700 }}
                >
                  Save online booking settings
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {canManageInvoicePayments ? (
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid', borderColor: 'secondary.light' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #7b1fa220 0%, #7b1fa210 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#7b1fa2',
                    }}
                  >
                    <PaymentIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Invoice payments
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Bank details for public pay link pages and optional Stripe Checkout. Super Admin and Admin only.
                    </Typography>
                  </Box>
                </Box>

                {invoicePaySettingsLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
                {invoicePaySettingsError ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {invoicePaySettingsError}
                    {' '}
                    You can still edit and save below.
                  </Alert>
                ) : null}

                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Bank transfer
                </Typography>
                <TextField
                  fullWidth
                  label="Bank name"
                  value={invoicePayBankName}
                  onChange={(e) => setInvoicePayBankName(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={invoicePaySettingsLoading}
                />
                <TextField
                  fullWidth
                  label="Sort code"
                  value={invoicePayBankSortCode}
                  onChange={(e) => setInvoicePayBankSortCode(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={invoicePaySettingsLoading}
                />
                <TextField
                  fullWidth
                  label="Account number"
                  value={invoicePayBankAccount}
                  onChange={(e) => setInvoicePayBankAccount(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={invoicePaySettingsLoading}
                />
                <TextField
                  fullWidth
                  label="Payment reference hint"
                  value={invoicePayBankReferenceHint}
                  onChange={(e) => setInvoicePayBankReferenceHint(e.target.value)}
                  helperText="Shown to clients when paying by bank transfer"
                  sx={{ mb: 2 }}
                  disabled={invoicePaySettingsLoading}
                />
                <TextField
                  fullWidth
                  label="Payment instructions"
                  value={invoicePayInstructions}
                  onChange={(e) => setInvoicePayInstructions(e.target.value)}
                  multiline
                  minRows={2}
                  sx={{ mb: 2 }}
                  disabled={invoicePaySettingsLoading}
                />

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Stripe (card payments)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Set <code>STRIPE_SECRET_KEY</code> and <code>STRIPE_WEBHOOK_SECRET</code> in backend <code>.env</code>.
                  Webhook URL: <code>POST /api/webhooks/stripe</code>
                </Typography>
                <List sx={{ p: 0, mb: 2 }}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Enable Stripe Checkout on pay links"
                      secondary={
                        invoicePayStripeConfigured
                          ? 'Stripe secret key is configured in .env'
                          : 'Add STRIPE_SECRET_KEY to .env first'
                      }
                    />
                    <Switch
                      checked={invoicePayStripeEnabled}
                      onChange={(e) => setInvoicePayStripeEnabled(e.target.checked)}
                      color="primary"
                      disabled={invoicePaySettingsLoading || !invoicePayStripeConfigured}
                    />
                  </ListItem>
                </List>

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveInvoicePayments}
                  disabled={loading || invoicePaySettingsLoading || !invoicePaySettingsLoaded}
                  sx={{ fontWeight: 700 }}
                >
                  Save invoice payment settings
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {canManageInvoicePayments ? (
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid', borderColor: 'info.light' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #0288d120 0%, #0288d110 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#0288d1',
                    }}
                  >
                    <PaletteIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Public brand
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Company details on invoice PDFs, plus logo and colors for booking, client portal, and pay links. Super Admin and Admin only.
                    </Typography>
                  </Box>
                </Box>

                {brandSettingsLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
                {brandSettingsError ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {brandSettingsError}
                  </Alert>
                ) : null}

                <TextField
                  fullWidth
                  label="Company name (on invoices & public pages)"
                  value={brandCompanyName}
                  onChange={(e) => setBrandCompanyName(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={brandSettingsLoading}
                />
                <TextField
                  fullWidth
                  label="Company address"
                  value={brandCompanyAddress}
                  onChange={(e) => setBrandCompanyAddress(e.target.value)}
                  multiline
                  minRows={2}
                  sx={{ mb: 2 }}
                  disabled={brandSettingsLoading}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <TextField
                    label="Company email"
                    value={brandCompanyEmail}
                    onChange={(e) => setBrandCompanyEmail(e.target.value)}
                    sx={{ flex: '1 1 200px' }}
                    disabled={brandSettingsLoading}
                  />
                  <TextField
                    label="Company phone"
                    value={brandCompanyPhone}
                    onChange={(e) => setBrandCompanyPhone(e.target.value)}
                    sx={{ flex: '1 1 160px' }}
                    disabled={brandSettingsLoading}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <TextField
                    label="Company registration no."
                    value={brandCompanyReg}
                    onChange={(e) => setBrandCompanyReg(e.target.value)}
                    sx={{ flex: '1 1 200px' }}
                    disabled={brandSettingsLoading}
                  />
                  <TextField
                    label="VAT number"
                    value={brandCompanyVat}
                    onChange={(e) => setBrandCompanyVat(e.target.value)}
                    sx={{ flex: '1 1 160px' }}
                    disabled={brandSettingsLoading}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Logo URL"
                  value={brandLogoUrl}
                  onChange={(e) => setBrandLogoUrl(e.target.value)}
                  helperText="Full HTTPS URL to a PNG or SVG (optional)"
                  sx={{ mb: 2 }}
                  disabled={brandSettingsLoading}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <TextField
                    label="Primary color"
                    type="color"
                    value={brandPrimaryColor}
                    onChange={(e) => setBrandPrimaryColor(e.target.value.toUpperCase())}
                    sx={{ width: 72 }}
                    disabled={brandSettingsLoading}
                  />
                  <TextField
                    label="Primary hex"
                    value={brandPrimaryColor}
                    onChange={(e) => setBrandPrimaryColor(e.target.value.toUpperCase())}
                    sx={{ flex: '1 1 120px' }}
                    disabled={brandSettingsLoading}
                  />
                  <TextField
                    label="Accent"
                    type="color"
                    value={brandAccentColor}
                    onChange={(e) => setBrandAccentColor(e.target.value.toUpperCase())}
                    sx={{ width: 72 }}
                    disabled={brandSettingsLoading}
                  />
                  <TextField
                    label="Accent hex"
                    value={brandAccentColor}
                    onChange={(e) => setBrandAccentColor(e.target.value.toUpperCase())}
                    sx={{ flex: '1 1 120px' }}
                    disabled={brandSettingsLoading}
                  />
                  <TextField
                    label="Background"
                    type="color"
                    value={brandBackgroundColor}
                    onChange={(e) => setBrandBackgroundColor(e.target.value.toUpperCase())}
                    sx={{ width: 72 }}
                    disabled={brandSettingsLoading}
                  />
                  <TextField
                    label="Background hex"
                    value={brandBackgroundColor}
                    onChange={(e) => setBrandBackgroundColor(e.target.value.toUpperCase())}
                    sx={{ flex: '1 1 120px' }}
                    disabled={brandSettingsLoading}
                  />
                </Box>
                <Box
                  style={brandHeaderStyle({
                    company_name: brandCompanyName,
                    primary_color: brandPrimaryColor,
                    accent_color: brandAccentColor,
                    background_color: brandBackgroundColor,
                  })}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 800 }}>{brandCompanyName || 'Preview'}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Header gradient preview (same as on public pages)
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSavePublicBrand}
                  disabled={loading || brandSettingsLoading || !brandSettingsLoaded}
                  sx={{ fontWeight: 700 }}
                >
                  Save public brand
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {canManageInvoicePayments ? (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #0ea5e920 0%, #0ea5e910 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#0ea5e9',
                    }}
                  >
                    <EmailOutlinedIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Outbound SMS &amp; email (manual)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allow office staff to send SMS/email from jobs. Twilio and MAIL_* stay in server .env.
                    </Typography>
                  </Box>
                </Box>

                {outboundSettingsLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
                {outboundSettingsError ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {outboundSettingsError}
                  </Alert>
                ) : null}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Chip
                    size="small"
                    label={outboundTwilioOk ? 'Twilio configured' : 'Twilio not configured'}
                    color={outboundTwilioOk ? 'success' : 'default'}
                  />
                  <Chip
                    size="small"
                    label={outboundMailOk ? 'Mail configured' : 'Mail not configured (log driver)'}
                    color={outboundMailOk ? 'success' : 'default'}
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Enable manual SMS from Jobs</Typography>
                  <Switch
                    checked={outboundSmsEnabled}
                    onChange={(e) => setOutboundSmsEnabled(e.target.checked)}
                    disabled={outboundSettingsLoading}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Enable manual email from Jobs</Typography>
                  <Switch
                    checked={outboundEmailEnabled}
                    onChange={(e) => setOutboundEmailEnabled(e.target.checked)}
                    disabled={outboundSettingsLoading}
                  />
                </Box>

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveOutboundMessaging}
                  disabled={loading || outboundSettingsLoading}
                >
                  Save outbound messaging
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {canManageInvoicePayments ? (
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid', borderColor: 'warning.light' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #f59e0b20 0%, #f59e0b10 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#f59e0b',
                    }}
                  >
                    <RateReviewIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Google reviews (reputation)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      After a job is marked completed, clients receive an email or SMS with your Google review link.
                      Creates/updates a system automation rule automatically.
                    </Typography>
                  </Box>
                </Box>

                {reputationSettingsLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
                {reputationSettingsError ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {reputationSettingsError}
                  </Alert>
                ) : null}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Send review requests automatically</Typography>
                  <Switch
                    checked={reputationEnabled}
                    onChange={(e) => setReputationEnabled(e.target.checked)}
                    disabled={reputationSettingsLoading}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Google review URL"
                  value={reputationReviewUrl}
                  onChange={(e) => setReputationReviewUrl(e.target.value)}
                  placeholder="https://g.page/r/..."
                  helperText="Your public Google review link (from Google Business Profile)"
                  sx={{ mb: 2 }}
                  disabled={reputationSettingsLoading}
                />

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <TextField
                    label="Delay after job completed (minutes)"
                    type="number"
                    value={reputationDelayMinutes}
                    onChange={(e) => setReputationDelayMinutes(e.target.value)}
                    helperText="Default 1440 = 24 hours"
                    sx={{ flex: '1 1 200px' }}
                    disabled={reputationSettingsLoading}
                  />
                  <FormControl sx={{ flex: '1 1 160px' }} disabled={reputationSettingsLoading}>
                    <InputLabel id="reputation-channel">Channel</InputLabel>
                    <Select
                      labelId="reputation-channel"
                      label="Channel"
                      value={reputationChannel}
                      onChange={(e) => setReputationChannel(e.target.value)}
                    >
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="sms">SMS (Twilio)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {reputationRuleId ? (
                  <Chip
                    size="small"
                    label={`Automation rule #${reputationRuleId} (edit in Automations)`}
                    sx={{ mb: 2 }}
                  />
                ) : null}

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Thank-you page for emails: /public/review/thanks — use {'{{review_url}}'} in other templates too.
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveReputation}
                  disabled={loading || reputationSettingsLoading || !reputationSettingsLoaded}
                  sx={{ fontWeight: 700 }}
                >
                  Save reputation settings
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {canManageInvoicePayments ? (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #6366f120 0%, #6366f110 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6366f1',
                    }}
                  >
                    <WebhookIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Outbound webhooks (Zapier / Make)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Send signed HTTP POST requests when jobs complete, estimates are accepted, or invoices are paid.
                    </Typography>
                  </Box>
                </Box>

                {webhookSettingsLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
                {webhookSettingsError ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {webhookSettingsError}
                  </Alert>
                ) : null}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Enable outbound webhooks</Typography>
                  <Switch
                    checked={webhookEnabled}
                    onChange={(e) => setWebhookEnabled(e.target.checked)}
                    disabled={webhookSettingsLoading}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Webhook URL"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://hooks.zapier.com/hooks/catch/..."
                  sx={{ mb: 2 }}
                  disabled={webhookSettingsLoading}
                />

                <TextField
                  fullWidth
                  label="Signing secret"
                  type="password"
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder={webhookSecretSet ? '•••••••• (leave blank to keep)' : 'Min 8 characters'}
                  helperText="HMAC-SHA256 in X-Workwise-Signature header (sha256=...)"
                  sx={{ mb: 2 }}
                  disabled={webhookSettingsLoading}
                />

                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Active events
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {['job.completed', 'estimate.accepted', 'invoice.paid'].map((ev) => (
                    <Chip
                      key={ev}
                      label={ev}
                      color={webhookEvents.includes(ev) ? 'primary' : 'default'}
                      variant={webhookEvents.includes(ev) ? 'filled' : 'outlined'}
                      onClick={() => toggleWebhookEvent(ev)}
                      disabled={webhookSettingsLoading}
                    />
                  ))}
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveWebhooks}
                    disabled={loading || webhookSettingsLoading || !webhookSettingsLoaded}
                    sx={{ fontWeight: 700 }}
                  >
                    Save webhook settings
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleWebhookTestPing}
                    disabled={webhookTestLoading || webhookSettingsLoading || !webhookSettingsLoaded}
                  >
                    {webhookTestLoading ? 'Sending…' : 'Send test ping'}
                  </Button>
                </Box>

                {webhookDeliveries.length > 0 ? (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Recent deliveries
                    </Typography>
                    <List dense>
                      {webhookDeliveries.map((d) => (
                        <ListItem key={d.id} disablePadding sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={`${d.event} — ${d.status}`}
                            secondary={`#${d.id} · attempts ${d.attempts}/${d.max_attempts}${d.last_status_code ? ` · HTTP ${d.last_status_code}` : ''}${d.last_error ? ` · ${d.last_error}` : ''}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                ) : null}
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {canManageInvoicePayments ? (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #0ea5e920 0%, #0ea5e910 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#0ea5e9',
                    }}
                  >
                    <CalendarIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Google Calendar (ICS feed)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Subscribe in Google Calendar with a secret URL. Scheduled jobs sync read-only (refreshes about every few hours).
                    </Typography>
                  </Box>
                </Box>

                {calendarSettingsLoading ? <LinearProgress sx={{ mb: 2 }} /> : null}
                {calendarSettingsError ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {calendarSettingsError}
                  </Alert>
                ) : null}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Enable calendar feed</Typography>
                  <Switch
                    checked={calendarEnabled}
                    onChange={(e) => setCalendarEnabled(e.target.checked)}
                    disabled={calendarSettingsLoading}
                  />
                </Box>

                {calendarFeedUrl ? (
                  <TextField
                    fullWidth
                    label="ICS subscription URL"
                    value={calendarFeedUrl}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 2 }}
                    disabled={calendarSettingsLoading}
                  />
                ) : null}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {calendarFeedUrl ? (
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(calendarFeedUrl);
                          setCalendarFeedCopied(true);
                          setTimeout(() => setCalendarFeedCopied(false), 2500);
                        } catch {
                          /* ignore */
                        }
                      }}
                      disabled={calendarSettingsLoading}
                    >
                      {calendarFeedCopied ? 'Copied' : 'Copy feed URL'}
                    </Button>
                  ) : null}
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleRotateCalendarToken}
                    disabled={loading || calendarSettingsLoading || !calendarSettingsLoaded}
                  >
                    Regenerate URL
                  </Button>
                </Box>

                <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={calendarSettingsLoading}>
                  <InputLabel id="calendar-tz-label">Event time zone</InputLabel>
                  <Select
                    labelId="calendar-tz-label"
                    label="Event time zone"
                    value={calendarTimezone}
                    onChange={(e) => setCalendarTimezone(e.target.value)}
                  >
                    {!TIMEZONE_CHOICES.includes(calendarTimezone) && (
                      <MenuItem value={calendarTimezone}>{calendarTimezone}</MenuItem>
                    )}
                    {TIMEZONE_CHOICES.map((tz) => (
                      <MenuItem key={tz} value={tz}>
                        {tz}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <TextField
                    label="Days ahead"
                    type="number"
                    value={calendarDaysAhead}
                    onChange={(e) => setCalendarDaysAhead(e.target.value)}
                    sx={{ flex: '1 1 120px' }}
                    disabled={calendarSettingsLoading}
                  />
                  <TextField
                    label="Days back"
                    type="number"
                    value={calendarDaysBack}
                    onChange={(e) => setCalendarDaysBack(e.target.value)}
                    sx={{ flex: '1 1 120px' }}
                    disabled={calendarSettingsLoading}
                  />
                </Box>

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Google Calendar → Add calendar → From URL → paste the ICS link. Cancelled jobs are excluded.
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveCalendar}
                  disabled={loading || calendarSettingsLoading || !calendarSettingsLoaded}
                  sx={{ fontWeight: 700 }}
                >
                  Save calendar settings
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {/* Security Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ 
                  width: 48, height: 48, borderRadius: '12px', 
                  background: 'linear-gradient(135deg, #ef444420 0%, #ef444410 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#ef4444'
                }}>
                  <SecurityIcon />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    Security Settings
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Change your password (Authenticator / SMS 2FA can be added later if needed).
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<SaveIcon />}
                    onClick={handleSaveSecurity}
                    disabled={loading}
                    sx={{
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      },
                    }}
                  >
                    Save Security Settings
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Appearance Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ 
                  width: 48, height: 48, borderRadius: '12px', 
                  background: 'linear-gradient(135deg, #8b5cf620 0%, #8b5cf610 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#8b5cf6'
                }}>
                  <PaletteIcon />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    Appearance Settings
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Dark / light theme — applies immediately; Save stores it on your account.
                  </Typography>
                </Box>
              </Box>

              <List sx={{ p: 0 }}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>Dark Mode</Typography>
                        {localDarkMode && (
                          <Chip 
                            label="On" 
                            size="small" 
                            color="primary" 
                            sx={{ fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    }
                    secondary="Same as the theme button in the sidebar menu"
                  />
                  <Switch
                    checked={localDarkMode}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setLocalDarkMode(v);
                      updateSettings(v);
                    }}
                    color="primary"
                  />
                </ListItem>
              </List>

              <Button
                variant="contained"
                fullWidth
                startIcon={<SaveIcon />}
                onClick={handleSaveAppearance}
                disabled={loading}
                sx={{
                  mt: 3,
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                  },
                }}
              >
                Save theme to account
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Regional & table density */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Time zone &amp; tables
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Interface language is English. Set your time zone for reference and choose tighter table rows on list pages.
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="tz-label">Time zone</InputLabel>
                <Select
                  labelId="tz-label"
                  label="Time zone"
                  value={timezone}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTimezone(v);
                    localStorage.setItem('appTimezone', v);
                  }}
                >
                  {!TIMEZONE_CHOICES.includes(timezone) && (
                    <MenuItem value={timezone}>{timezone}</MenuItem>
                  )}
                  {TIMEZONE_CHOICES.map((tz) => (
                    <MenuItem key={tz} value={tz}>
                      {tz}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle2">Compact table rows</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Applies to Jobs, Invoices, Clients tables
                  </Typography>
                </Box>
                <Switch
                  checked={tableDensity === 'compact'}
                  onChange={(e) => {
                    const v = e.target.checked ? 'compact' : 'comfortable';
                    setTableDensity(v);
                    localStorage.setItem('tableDensity', v);
                  }}
                />
              </Box>
              <Button variant="contained" fullWidth startIcon={<SaveIcon />} onClick={handleSaveRegional} disabled={loading}>
                Save language &amp; region
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* User training guides */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{
                  width: 48, height: 48, borderRadius: '12px',
                  background: 'linear-gradient(135deg, #1565c020 0%, #1565c010 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#1565c0',
                }}>
                  <MenuBookIcon />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    User guides &amp; training
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Full manuals for office staff — same content as the printable PDF (all features, step by step).
                  </Typography>
                </Box>
              </Box>
              <List dense disablePadding>
                {USER_GUIDES.map((guide) => (
                  <ListItem
                    key={guide.id}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        component="a"
                        href={guide.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open ${guide.label}`}
                        edge="end"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemButton
                      component="a"
                      href={guide.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ borderRadius: 1, pr: 6 }}
                    >
                      <ListItemText
                        primary={guide.label}
                        secondary={guide.pdf ? 'PDF — download or print' : 'Opens in a new browser tab'}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      />
                      {guide.pdf ? (
                        <PictureAsPdfIcon sx={{ ml: 1, color: '#c62828', flexShrink: 0 }} />
                      ) : (
                        <MenuBookIcon sx={{ ml: 1, color: '#1565c0', flexShrink: 0, fontSize: 20 }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* System Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ 
                  width: 48, height: 48, borderRadius: '12px', 
                  background: 'linear-gradient(135deg, #10b98120 0%, #10b98110 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#10b981'
                }}>
                  <SettingsIcon />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    System Information
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Application version and system details
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Button variant="outlined" onClick={() => setShortcutsOpen(true)}>
                  Keyboard shortcuts (?)
                </Button>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, backgroundColor: 'rgba(16, 185, 129, 0.04)' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981' }}>
                      v{APP_VERSION}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      App Version
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, backgroundColor: 'rgba(59, 130, 246, 0.04)' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                      React {REACT_VERSION_LABEL}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Frontend Framework
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, backgroundColor: 'rgba(245, 158, 11, 0.04)' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                      Laravel {LARAVEL_VERSION_LABEL}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Backend Framework
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, backgroundColor: 'rgba(139, 92, 246, 0.04)' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                      MySQL
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Database (typical)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <KeyboardShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </Box>
  );
} 